import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ALLOWED_FILE_SHARE_VCT_TYPE, MAX_UPLOAD_SIZE, } from 'src/app/conf/user-files';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { Tooltip } from 'primeng/tooltip';
import { Notification, NotificationLevel } from 'src/app/model/notification';
import * as NotificationsActions from 'src/app/store/notifications/notifications.actions';
import { StandardPageLayout } from '../../shared/stdlayoutlib/classes.stdlayoutlib';
import { Plant } from 'src/app/model/device-vct/device-vct-implant';
import { Observable, Subscription } from 'rxjs';
import { selectVctPlantsList } from 'src/app/store/device-vct/device-vct.selector';
import { DevicesVctFeatures, DevicesVctGenericType, DevicesVctModules, ImportFileSharingSuccess } from 'src/app/conf/devices-vct';
import * as AnalyticsActions from 'src/app/store/analytics/analytics.actions';

@Component({
	templateUrl: './share-device-vct-upload.dialog.component.html',
	providers: [ConfirmationService, DialogService]
})

export class ShareDeviceVctUploadDialogComponent implements OnInit, OnDestroy {
	@ViewChild('fileUpload') fileUpload: any;

	plantsList$: Observable<Plant[] | undefined>;
	plantsListSub: Subscription | undefined;

	plantsList: Plant[];

	labelFile: string;

	text: string | ArrayBuffer;
	messageErrorUpload = "";
	@ViewChild('imageInput', { static: false }) imageInput: ElementRef;

	totalRecords = 0;

	processText: string;

	mapFileData = new Map<string, string>();

	// Upload File
	fileToUpload: File;
	uploadedFile: string | ArrayBuffer;

	maxUploadSize = MAX_UPLOAD_SIZE;

	ImportFileSharingSuccess = ImportFileSharingSuccess;
	importFileSuccess = ImportFileSharingSuccess.NOT_UPLOADED; // -1: not imported due to block errors, 0: not uploaded, 1: imported

	acceptButtonStyle: string;
	rejectButtonStyle: string;

	showUploadButton = false;
	reuploadButton: boolean;

	screenWidth: number;

	@ViewChild(Tooltip) tooltip!: Tooltip;

	constructor(
		private store: Store<AppState>,
		private ref: DynamicDialogRef,
		public config: DynamicDialogConfig,
		private http: HttpClient,
		private translate: TranslateService,
		public confirmationService: ConfirmationService,
	) {
		this.initSelectors();
	}

	ngOnInit(): void {
		this.subscribeAll();
		this.onResize();
	}

	initSelectors(): void {
		this.plantsList$ = this.store.select(selectVctPlantsList);
		this.uploadInit();
	}

	showHideTooltip() {
		this.tooltip.activate();
	}

	subscribeAll(): void {
		this.plantsListSub = this.plantsList$?.subscribe(plantsList => {
			this.plantsList = plantsList;
		});
	}

	ngOnDestroy(): void {
		this.plantsListSub?.unsubscribe();
	}

	closeDialog(): void {
		this.ref.close(this.importFileSuccess);
	}

	handleFileInput(event) {
		this.processText = '';
		if (event.currentFiles.length > 0) {
			this.fileToUpload = event.currentFiles[0];
			if (!ALLOWED_FILE_SHARE_VCT_TYPE.includes(this.fileToUpload.type)) {
				const notification = new Notification("FILE_NOT_COMPATIBLE", this.translate.instant("MAX_UPLOAD_SIZE_1") + " 900KB", NotificationLevel.Error, '');
				this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));

				this.fileToUpload = null;
				this.reuploadButton = true;
			} else if (this.fileToUpload.size > MAX_UPLOAD_SIZE) {
				const notification = new Notification("FILE_TOO_BIG", this.translate.instant("MAX_UPLOAD_SIZE_1") + " 900KB", NotificationLevel.Error, '');
				this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));

				this.fileToUpload = null;
				this.reuploadButton = true;
			} else {
				const myReader: FileReader = new FileReader();
				myReader.onloadend = () => {
					this.processText = this.processText + this.translate.instant('VCT_UPLOAD_FILE_UPLOAD') + '....\n';
					this.text = myReader.result;
					this.elaborateFile(this.text.toString());
				};
				myReader.readAsText(this.fileToUpload);
				this.labelFile = this.fileToUpload.name;
				this.showUploadButton = true;

				/*myReader.onloadend = (e) => {
					console.log(myReader.result);
				}
				myReader.readAsText(this.file);*/
			}
		}
	}

	elaborateFile(file: string): void {
		this.processText = this.processText + this.translate.instant('VCT_UPLOAD_READING_PROGRESS') + '....\n';
		let lineCont = 0;
		this.mapFileData = new Map();
		for (const line of file.split(/[\r\n]+/)) {
			lineCont++;
			if (lineCont > 4) {
				// console.log(lineCont + ' -- ' + line);
				if (this.checkLine(line)) {
					this.processText = this.processText + this.translate.instant('VCT_UPLOAD_ERRORE_ROW') + ': ' + lineCont + ' \n';
					break;
				}
			}
		}
		this.processText = this.processText + this.translate.instant('VCT_UPLOAD_READING_DONE') + '\n';
		this.processText = this.processText + this.translate.instant('VCT_UPLOAD_FILE_PROCESSING_PROGRESS') + '....\n';
		lineCont = 0;
		for (const line of file.split(/[\r\n]+/)) {
			lineCont++;
			if (lineCont > 4) {
				if (this.elaborateLine(line)) {
					this.processText = this.processText + this.translate.instant('VCT_UPLOAD_ERRORE_ROW') + ': ' + lineCont + ' \n';
					break;
				}
			}
		}
		this.processText = this.processText + this.translate.instant('VCT_UPLOAD_FILE_PROCESSING_DONE') + '\n';
		const writeStruct = this.writeStruct();
		if (writeStruct) {
			this.importFileSuccess = ImportFileSharingSuccess.IMPORTED_SUCCESS; // import success
			setTimeout(() => {
				this.store.dispatch(AnalyticsActions.eventNewAction({ eventName: 'import_vct_share_success_noerrors', params: { page_title: 'List Device | VCT' } }));
			}, 2000);
		} else {
			this.importFileSuccess = ImportFileSharingSuccess.NOT_IMPORTED_BLOCK_ERRORS; // not imported due to blocked error
			setTimeout(() => {
				this.store.dispatch(AnalyticsActions.eventNewAction({ eventName: 'import_vct_share_failed_witherrors', params: { page_title: 'List Device | VCT' } }));
			}, 2000);
		}
	}

	checkLine(line: string): boolean {
		const splitLine = line.split(',');
		if (splitLine.length > 4 || splitLine.length < 4 || !splitLine[3].includes('##') || !splitLine[3].includes('**')) {
			return true;
		}
		return false;
	}

	elaborateLine(line: string): boolean {
		const splitLine = line.split(',');
		this.mapFileData.set(splitLine[0], splitLine[3]);
		return false;
	}

	writeStruct(): boolean {
		this.processText = this.processText + this.translate.instant('VCT_UPLOAD_WRITE_DATA_PROGRESS') + '....\n';
		const plant = this.plantsList[0];
		if (this.mapFileData.has(plant.Id + '_' + DevicesVctGenericType.PLANT)) {
			this.plantsList[0].OwnerEmail = this.mapFileData.get(plant.Id + '_' + DevicesVctGenericType.PLANT).replace('##', '').replace('**', '');
		} else {
			this.processText = this.processText + this.translate.instant('VCT_UPLOAD_ERROR_PLANT_NOT_FOUND') + ' ---Id:' + plant.Id + ' ---' +
				this.translate.instant('NAME') + ':' + plant.Name + '\n';
			return false;
		}
		for (const module of plant.Modules) {
			if (module.ModuleId === DevicesVctModules.VideoUnit) {
				if (this.mapFileData.has(module.Id + '_' + DevicesVctGenericType.VIDEOUNIT)) {
					module.OwnerEmail = this.mapFileData.get(module.Id + '_' + DevicesVctGenericType.VIDEOUNIT).replace('##', '').replace('**', '');
					module.WarningError = false;
					if (!this.isEmailValid(module.OwnerEmail)) {
						module.WarningError = true;
						module.WarningErrorType = 'VCT_MAIL_NOT_VALID';
						this.processText = this.processText + this.translate.instant('VCT_UPLOAD_ERROR_MAIL_NOT_VALID') + ' ---Id:' + module.Id + ' ---' +
							this.translate.instant('NAME') + ':' + module.Name + '\n';
					}
				} else {
					this.processText = this.processText + this.translate.instant('VCT_UPLOAD_ERROR_UNIT_NOT_FOUND') + ' ---Id:' + module.Id + ' ---' +
						this.translate.instant('NAME') + ':' + module.Name + '\n';
					module.WarningError = true;
					module.WarningErrorType = 'VCT_UNIT_NOT_FOUND';
				}
			}
			if (module.Features !== undefined && module.Features.length > 0) {
				for (const feature of module.Features) {
					if (feature.FeatureId === DevicesVctFeatures.App) {
						if (this.mapFileData.has(feature.Id + '_' + DevicesVctGenericType.APP)) {
							feature.SipAccount.UserEmail = this.mapFileData.get(feature.Id + '_' + DevicesVctGenericType.APP).replace('##', '').replace('**', '');
							feature.WarningError = false;
							if (!this.isEmailValid(feature.SipAccount.UserEmail)) {
								feature.WarningError = true;
								feature.WarningErrorType = 'VCT_MAIL_NOT_VALID';
								this.processText = this.processText + this.translate.instant('VCT_UPLOAD_ERROR_MAIL_NOT_VALID') + ' ---Id:' + feature.Id + ' ---' +
									this.translate.instant('NAME') + ':' + feature.Name + '\n';
							}
						} else {
							this.processText = this.processText + this.translate.instant('VCT_UPLOAD_ERROR_APP_NOT_FOUND') + ' ---Id:' + feature.Id + ' ---' +
								this.translate.instant('NAME') + ':' + feature.Name + '\n';
							feature.WarningError = true;
							feature.WarningErrorType = 'VCT_APP_NOT_FOUND';
						}

					}
				}
			}
		}
		this.processText = this.processText + this.translate.instant('VCT_UPLOAD_WRITE_DATA_DONE') + '\n';
		return true;
	}

	isEmailValid(email: string): boolean {
		if (email === '') {
			return true;
		}
		const re = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/;
		return re.test(email.toLowerCase());
	}

	onUploadClick(): void {
		/*const ver = btoa(this.image.toString());
		console.log(ver);
		this.fileToUpload = null;
		this.showUploadButton = false;
	
		this.reuploadButton = true;*/
	}

	uploadInit() {
		this.reuploadButton = false;
	}

	setReupload() {
		this.reuploadButton = false;
		this.fileToUpload = null;
	}

	// Design
	@HostListener('window:resize')
	onResize(): void {
		this.screenWidth = window.innerWidth;
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-20rem');
	}

	simpleInputTextLayout(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}
}